import { Controller } from "@hotwired/stimulus"

// Shows/hides the campaign-scoped Insights extras on a campaign card -- the
// "see what the client sees" link, and the derived MISSION: ON / NOT SET pill
// (card #3602) -- the moment the Insights toggle beside them is switched,
// without a reload.
//
// Both hang off the same campaign flag, so they reveal and hide together. The
// pill joined this controller rather than being conditionally rendered for the
// same reason the link did: revealing one and leaving the other stale until a
// reload is worse than either behaviour on its own.
//
// CampaignsController#toggle_insights responds with a turbo_stream replacing
// campaign_<id>_insights -- the shared campaigns/_insights_cell partial's own
// root. The link is NOT inside that partial (it is planning-side markup
// sitting next to it), so the stream alone would flip the checkbox and leave
// the link stale. That controller and partial are shared and unmodified; this
// listens for the stream rather than changing what it sends.
//
// The new state is read from the incoming markup's own checkmark rather than
// from a class name: campaigns/_insights_cell renders an <svg> tick inside the
// box only when insights are enabled, so its presence is the state.
export default class extends Controller {
  static targets = ["link", "missionState"]
  static values = { campaignId: Number }

  connect() {
    this.onStream = this.handleStream.bind(this)
    document.addEventListener("turbo:before-stream-render", this.onStream)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.onStream)
  }

  handleStream(event) {
    const stream = event.detail?.newStream
    if (!stream) return
    if (stream.getAttribute("target") !== `campaign_${this.campaignIdValue}_insights`) return

    const incoming = stream.querySelector("template")?.content
    if (!incoming) return

    const enabled = incoming.querySelector("svg") !== null
    requestAnimationFrame(() => this.applyEnabled(enabled))
  }

  applyEnabled(enabled) {
    this.revealTargets.forEach((element) => element.classList.toggle("hidden", !enabled))
  }

  // Every element whose visibility follows the campaign flag. Built from the
  // targets rather than a querySelectorAll so a card that renders only one of
  // them still works.
  get revealTargets() {
    return [...this.linkTargets, ...this.missionStateTargets]
  }
}
